import React, { useState, useEffect, useMemo, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import SubTitle from "../common/SubTitle";
import Cards from "../common/Cards";
import SearchFilter from "../common/SearchFilter";
import Pagination from "../common/Pagination";
import axios from "axios";
import config from "../../config.js";
import { usePermission } from "../../hooks/usePermission";
import { useUser } from "../../pages/login/UserContextPersistent";
import InactiveInventoryTable from "../common/InactiveInventoryTable";
import DeleteModal from "../common/DeleteModal";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
// import DynamicTable from "../common/DynamicTable";
import { useTranslation } from "react-i18next";
import { CARD_BACKGROUND_COLORS, TEXT_COLORS } from "../../constants/styles";
import { ACTIONS_COLUMN_ID } from "../../constants/dashboardConstants";
import { use } from "react";
import { Box, CircularProgress, IconButton } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  PAGE_IDS,
  isJsonDrivenLayoutEnabled,
} from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import {
  FLEET_USER_ROW_ACTIONS,
  computeFleetUserMetric,
} from "../../config/usersModuleCatalog";
import { hasLayoutPermission } from "../../config/vehiclesModuleCatalog";
import {
  buildFleetRowActionsFromLayout,
  buildFleetUserStatCards,
  buildFilterWidgetsFromLayout,
  buildTableHeadersFromLayout,
  getActionBarWidgets,
  getPageTitleKey,
} from "../../layout/moduleLayoutUtils";
import SubTitleNew from "../common/New/SubTitleNew.jsx";
import StatsCard from "./StatsCard.jsx";
import DynamicTable from "../common/New/DynamicTable.jsx";
import { TableStatusBadge } from "../common/New/DynamicTable.jsx";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import "./UsersMain.css";
import AddUser from "../../pages/Users/AddUser.js";

const legacyHeaders = [
  { key: "name", value: "name" },
  { key: "email", value: "email" },
  { key: "username", value: "username" },
  { key: "roles", value: "roles" },
  { key: ACTIONS_COLUMN_ID, value: "Actions" },
];

export default function UsersMain({ school = false } = {}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [userData, setUserData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    role: "",
    status: "",
    department: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pass, setPass] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordError, setPasswordError] = useState("");

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const { organizationInactive } = useUser();
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const usersPageId = school ? PAGE_IDS.SCHOOL_USERS : PAGE_IDS.FLEET_USERS;
  const { layout: moduleLayout } = usePageLayout(
    useJsonLayout ? usersPageId : null,
  );

  const layoutHeaders = useMemo(() => {
    if (!moduleLayout) return null;
    return buildTableHeadersFromLayout(moduleLayout, { hasPermission, t });
  }, [moduleLayout, hasPermission, t]);

  const layoutRowActions = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetRowActionsFromLayout(
      moduleLayout,
      { hasPermission },
      FLEET_USER_ROW_ACTIONS,
    );
  }, [moduleLayout, hasPermission]);

  const layoutStatCards = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFleetUserStatCards(moduleLayout, userData);
  }, [moduleLayout, userData]);

  const actionBarWidgets = useMemo(
    () => (moduleLayout ? getActionBarWidgets(moduleLayout) : []),
    [moduleLayout],
  );

  const layoutFilterWidgets = useMemo(() => {
    if (!moduleLayout) return null;
    return buildFilterWidgetsFromLayout(moduleLayout);
  }, [moduleLayout]);

  const tableHeaders = layoutHeaders || legacyHeaders;
  const downloadColumns = tableHeaders
    .filter((header) => header.key !== ACTIONS_COLUMN_ID)
    .map((header) => ({
      key: header.key,
      label: header.value,
    }));

  const canUpdateUser = hasPermission("/api/user", "PUT");
  const canDeleteUser = hasPermission("/api/user", "DELETE");
  const canViewUserDetails = hasPermission("/api/user/id", "GET");
  const canResetPassword = hasPermission("/api/user/password", "PATCH");
  const canUpdateRole = hasPermission("/api/user/role", "PATCH");

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [totalRoleCount, setTotalRoleCount] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [visibleUserCount, setVisibleUserCount] = useState(10);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);

  const [filterConfig, setFilterConfig] = useState([
    {
      id: "role",
      label: "roles",
      helperText: "Select role",
      options: [],
    },
  ]);

  const getLegacyActions = () =>
    Object.values(FLEET_USER_ROW_ACTIONS).filter((action) =>
      hasLayoutPermission(action.permission, hasPermission),
    );

  const tableActions = layoutRowActions || getLegacyActions();

  useEffect(() => {
    if (layoutFilterWidgets) {
      setFilterConfig(
        layoutFilterWidgets.map((widget) => ({
          id: widget.filterKey,
          label: widget.labelKey,
          helperText: `Select ${String(widget.labelKey || "").toLowerCase()}`,
          options:
            widget.filterKey === "role"
              ? roleOptions
              : (widget.options || []).map((option) => ({
                  value: option.value,
                  label: option.label,
                })),
        })),
      );
      return;
    }

    setFilterConfig([
      {
        id: "role",
        label: "roles",
        helperText: "Select role",
        options: roleOptions,
      },
    ]);
  }, [layoutFilterWidgets, roleOptions]);

  // Function to fetch all users
  async function getAllUsers() {
    try {
      setIsLoading(true);
      const userState = JSON.parse(localStorage.getItem("userState"));
      const organizationId = userState.user.organizationId;
      const response = await axios.get(
        `${config.baseURL}/user/organization/${organizationId}`,
        {
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.log("Error in fetching user data: ", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }

  async function getAllRoles() {
    try {
      const response = await axios.get(`${config.baseURL}/role/organization/`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error("Error in fetching role data: ", error);
      throw error;
    }
  }

  // Function to delete user
  async function deleteUser(id) {
    if (!canDeleteUser) return;

    try {
      const response = await axios.delete(`${config.baseURL}/user/${id}`, {
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.log("Error in Deleting user data: ", error);
      throw error;
    }
  }

  // Function to update password
  async function updatePassword(id, confirmPass) {
    if (!canResetPassword) return;

    try {
      const response = await axios.patch(
        `${config.baseURL}/user/password`,
        {
          userId: id,
          newPassword: confirmPass,
        },
        {
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error updating password: ", error);
      throw error;
    }
  }

  // Process user data for display
  function processUserData(userList) {
    return userList.map((user) => ({
      name: user.name || "N/A",
      email: user.email || "N/A",
      userName: user.userName || "N/A",
      id: user.id,
      role: user.roles ? user.roles.name : "N/A",
      // The users endpoint does not always include these fields.
      status: user.status ? String(user.status).toUpperCase() : null,
      department: user.department?.name || user.department || null,
      lastLogin: user.lastLogin || user.lastLoginAt || null,
      createdAt: user.createdAt || null,
      originalData: user,
    }));
  }

  // Fetch users data on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getAllUsers();
        const users = Array.isArray(response?.users) ? response.users : [];
        const processedUsers = processUserData(users);
        setUserData(processedUsers);

        // Extract unique roles for filter
        const uniqueRoles = [];
        const roleSet = new Set();

        users.forEach((user) => {
          if (user.roles && user.roles.name && !roleSet.has(user.roles.name)) {
            roleSet.add(user.roles.name);
            uniqueRoles.push({
              value: user.roles.name,
              label: user.roles.name,
            });
          }
        });

        setRoleOptions(uniqueRoles);

        try {
          const rolesResponse = await getAllRoles();
          const roles = Array.isArray(rolesResponse?.roles)
            ? rolesResponse.roles
            : Array.isArray(rolesResponse)
              ? rolesResponse
              : [];
          setTotalRoleCount(roles.length);
        } catch (roleError) {
          console.error("Error fetching all roles for total count:", roleError);
          setTotalRoleCount(uniqueRoles.length);
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.log("Unauthorized request");
          navigate("/");
          return;
        }
        console.error("Error fetching user data:", error);
      }
    };

    fetchUsers();
  }, [navigate, reloadKey]);

  // Format data for table display
  const formatDataForTable = (data) => {
    return data.map((user) => {
      const formattedData = {
        name: user.name,
        email: user.email,
        username: user.userName,
        roles: t(user.role),
      };

      // Add original ID for action handling
      formattedData.id = user.id;
      formattedData.originalData = user.originalData;

      return formattedData;
    });
  };

  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page on search
    setVisibleUserCount(10);
    setExpandedUserId(null);
  };

  // Handle filter changes
  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
    setCurrentPage(1); // Reset to first page on filter change
    setVisibleUserCount(10);
    setExpandedUserId(null);
  };

  // Filter data based on search query and filters
  const filteredUserData = userData.filter((user) => {
    // Check if role filter is applied
    if (filters.role && user.role !== filters.role) {
      return false;
    }

    // Check if status filter is applied
    if (filters.status && user.status !== filters.status) {
      return false;
    }
    if (filters.department && user.department !== filters.department) {
      return false;
    }

    // If no search query, return true (show all that match filters)
    if (!searchQuery) {
      return true;
    }

    // Otherwise, search in all relevant fields
    const query = searchQuery.toLowerCase();
    return (
      (user.name && user.name.toLowerCase().includes(query)) ||
      (user.email && user.email.toLowerCase().includes(query)) ||
      (user.userName && user.userName.toLowerCase().includes(query)) ||
      (user.role && user.role.toLowerCase().includes(query))
    );
  });
  const visibleUserData = filteredUserData.slice(0, visibleUserCount);
  const hasMoreUsers = visibleUserData.length < filteredUserData.length;

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage) => {
    setItemsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Add user handler
  const handleAddUser = () => {
    if (canCreateUser) {
      // Previous route flow: navigate(school ? "/school-users/add" : "/user/AddUser");
      setShowCreateUser((open) => !open);
      setExpandedUserId(null);
    } else {
      // You could show a permission error message here
      console.log("You don't have permission to create users");
    }
  };

  // Add role handler
  const handleAddRole = () => {
    if (canUpdateRole) {
      navigate(school ? "/school-users/role" : "/user/role");
    } else {
      // You could show a permission error message here
      console.log("You don't have permission to manage roles");
    }
  };

  // Delete modal handlers
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSelectedUser(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedUser && canDeleteUser) {
      try {
        await deleteUser(selectedUser.id);

        // Remove the deleted user from the state
        setUserData((prevData) =>
          prevData.filter((item) => item.id !== selectedUser.id),
        );
        window.location.reload();
      } catch (error) {
        console.log("Error deleting user: ", error);
      }
    }
    setIsDeleteModalOpen(false);
    setSelectedUser(null);
  };

  // Reset password modal handlers
  const handleResetPasswordCancel = () => {
    setIsResetPasswordModalOpen(false);
    setPass({
      newPassword: "",
      confirmPassword: "",
    });
  };

  const validatePassword = (password) => {
    if (!password) {
      return t("Password is required");
    }
    if (password.length < 6) {
      return t("Password must be at least 6 characters long");
    }
    if (!/[A-Z]/.test(password)) {
      return t("Password must contain at least one uppercase letter (A-Z)");
    }
    if (!/[a-z]/.test(password)) {
      return t("Password must contain at least one lowercase letter (a-z)");
    }
    if (!/[0-9]/.test(password)) {
      return t("Password must contain at least one number (0-9)");
    }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      return t("Password must contain at least one special character (e.g. @, #, $, !)");
    }
    return "";
  };

  const handleResetPasswordConfirm = async () => {
    const { newPassword, confirmPassword } = pass;

    if (!newPassword || !confirmPassword) {
      setPasswordError(t("Both fields are required"));
      return;
    }

    const passValidationErr = validatePassword(newPassword);
    if (passValidationErr) {
      setPasswordError(passValidationErr);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t("Passwords do not match"));
      return;
    }

    if (!canResetPassword) {
      setPasswordError(t("You don't have permission to reset passwords"));
      return;
    }

    try {
      await updatePassword(selectedUser.id, newPassword);
      setIsResetPasswordModalOpen(false);
      setPass({
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error) {
      console.error("Error updating password:", error);
    }
  };

  // Handle password change in form
  const handlePassChange = (e) => {
    const { name, value } = e.target;
    setPass((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setPasswordError("");
  };

  // Handle action click - with permission checks
  const handleActionClick = (actionId, user, actionObj) => {

    switch (actionId) {
      case "update":
        if (canUpdateUser) {
          // Previous route flow: navigate(school ? `/school-users/${user.id}` : actionObj.path, { state: { id: user.id } });
          setShowCreateUser(false);
          setExpandedUserId((id) => String(id) === String(user.id) ? null : user.id);
        }
        break;
      case "reset":
        if (canResetPassword) {
          setSelectedUser(user);
          setIsResetPasswordModalOpen(true);
        }
        break;
      case "delete":
        if (canDeleteUser) {
          setSelectedUser(user);
          setIsDeleteModalOpen(true);
        }
        break;
      default:
        break;
    }
  };

  // Calculate pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredUserData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredUserData.length / itemsPerPage);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setExpandedUserId(null);
    setCurrentPage(pageNumber);
  };

  const handleUserFormComplete = () => {
    setShowCreateUser(false);
    setExpandedUserId(null);
    setReloadKey((key) => key + 1);
  };

  // Get counts for cards
  // const activeUsersCount = userData.filter(user => user.status === "ACTIVE").length || 0;
  // const inactiveUsersCount = userData.filter(user => user.status === "INACTIVE").length || 0;

  const activeUsersCount = userData.filter((user) => user.status === "ACTIVE").length;
  const inactiveUsersCount = userData.filter((user) => user.status === "INACTIVE").length;
  const rolesCount = totalRoleCount || new Set(userData.map((user) => user.role).filter((role) => role && role !== "N/A")).size;
  const hasStatusData = userData.some((user) => user.status);
  const departmentOptions = [...new Set(userData.map((user) => user.department).filter(Boolean))];
  const formatDate = (value) => value && !Number.isNaN(Date.parse(value))
    ? new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(new Date(value))
    : "—";
  const formatLastLogin = (value) => {
    if (!value || Number.isNaN(Date.parse(value))) return "—";
    const date = new Date(value);
    const diffMs = Date.now() - date.getTime();
    if (diffMs < 0) return formatDate(value);
    const minutes = Math.floor(diffMs / 60000);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return minutes === 1 ? "1 min ago" : `${minutes} mins ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return hours === 1 ? "1 hr ago" : `${hours} hrs ago`;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
    if (date >= startOfYesterday && date < startOfToday) {
      const time = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
      return `Yesterday, ${time}`;
    }
    const days = Math.floor((startOfToday.getTime() - date.getTime()) / 86400000);
    if (days < 7) return days === 1 ? "1 day ago" : `${days} days ago`;
    return formatDate(value);
  };
  const displayHeaders = [
    { key: "name", label: "User", width: "22%", render: (row) => <div className="users-name-cell"><strong>{row.name}</strong><span>{row.email}</span></div> },
    { key: "role", label: "Role", width: "12%", render: (row) => row.role || "—" },
    { key: "department", label: "Department", width: "14%", render: (row) => row.department || "—" },
    { key: "status", label: "Status", width: "12%", render: (row) => row.status ? <TableStatusBadge tone={row.status === "ACTIVE" ? "green" : "gray"}>{row.status === "ACTIVE" ? t("Active") : t("Inactive")}</TableStatusBadge> : "—" },
    { key: "lastLogin", label: "Last Login", width: "16%", render: (row) => formatLastLogin(row.lastLogin) },
    { key: "createdAt", label: "Created On", width: "14%", render: (row) => formatDate(row.createdAt) },
  ];
  const displayActions = tableActions.map((action) => ({
    ...action,
    onClick: (row) => handleActionClick(action.id, row, action),
  }));

  const createAction = actionBarWidgets.find(
    (widget) => widget.actionKey === "createUser",
  );
  const manageRolesAction = actionBarWidgets.find(
    (widget) => widget.actionKey === "manageRoles",
  );
  const canCreateUser = createAction
    ? hasLayoutPermission(createAction.permission, hasPermission)
    : hasPermission("/api/user", "POST");
  const canManageRoles = manageRolesAction
    ? hasLayoutPermission(manageRolesAction.permission, hasPermission)
    : canUpdateRole;
  const pageTitle = getPageTitleKey(moduleLayout) || "Users";
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  const legacyStatCards = [
    {
      title: "totalUsers",
      value: computeFleetUserMetric("totalUsers", userData),
      backgroundColor: CARD_BACKGROUND_COLORS.DARK_BLUE,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
    {
      title: "activeUsers",
      value: activeUsersCount,
      backgroundColor: CARD_BACKGROUND_COLORS.GREEN,
      titleColor: TEXT_COLORS.WHITE,
      valueColor: TEXT_COLORS.WHITE,
    },
    {
      title: "inactiveUsers",
      value: inactiveUsersCount,
      backgroundColor: CARD_BACKGROUND_COLORS.YELLOW,
      titleColor: TEXT_COLORS.BLACK,
      valueColor: TEXT_COLORS.BLACK,
    },
  ];

  return (
    <div>
      {/* <SubTitle
        title={pageTitle}
        path={!school ? `${orgName || "organization"} > users` : undefined}
        add={!organizationInactive && canCreateUser ? createAction?.labelKey || "createUser" : null}
        addPath={school ? "/school-users/add" : "/user/AddUser"}
        onAddClick={!organizationInactive && canCreateUser ? handleAddUser : null}
        track={!organizationInactive && canManageRoles ? manageRolesAction?.labelKey || "manageRoles" : null}
        trackPath={school ? "/school-users/role" : "/user/role"}
        onTrackClick={!organizationInactive && canManageRoles ? handleAddRole : null}
      /> */}
      <SubTitleNew
        title={pageTitle}
        path={!school ? `${orgName || "organization"} > users` : undefined}
        add={
          !organizationInactive && canCreateUser
            ? createAction?.labelKey || "createUser"
            : null
        }
        addPath={school ? "/school-users/add" : "/user/AddUser"}
        onAddClick={
          !organizationInactive && canCreateUser ? handleAddUser : null
        }
        track={
          !organizationInactive && canManageRoles
            ? manageRolesAction?.labelKey || "manageRoles"
            : null
        }
        trackPath={school ? "/school-users/role" : "/user/role"}
        onTrackClick={
          !organizationInactive && canManageRoles ? handleAddRole : null
        }
        showDashboardControls={false}
      />

      {organizationInactive ? (
        <div className="table_container">
          <InactiveInventoryTable
            title="userInventory"
            headers={tableHeaders}
            data={formatDataForTable(filteredUserData || [])}
            loading={isLoading}
            emptyMessage={t("noDataFound")}
            itemLabel="users"
          />
        </div>
      ) : (
        <Box className="users-page-content">
          {showCreateUser && canCreateUser && <AddUser embedded onCancel={() => setShowCreateUser(false)} onComplete={handleUserFormComplete} />}
          {/* <Cards cardData={layoutStatCards || legacyStatCards} /> */}
          <div className="stats-cards-container">
            <StatsCard title={t("Users")} value={userData.length} icon={PeopleAltOutlinedIcon} tone="blue" />
            <StatsCard title={t("Active Users")} value={hasStatusData ? activeUsersCount : "—"} icon={PersonOutlineIcon} tone="blue" />
            <StatsCard title={t("Roles")} value={rolesCount} icon={AdminPanelSettingsOutlinedIcon} tone="blue" />
          </div>
          {/* <div className="table_container"> */}
          {/* <SearchFilter
              filters={filterConfig}
              searchPlaceholder="search"
              onSearchChange={handleSearchChange}
              onFilterChange={handleFilterChange}
              dataDownload={school}
              downloadRows={formatDataForTable(filteredUserData || [])}
              downloadColumns={downloadColumns}
              downloadFileName={school ? "school-users" : "users"}
            /> */}

          <div className={`users-table-wrapper${hasMoreUsers ? " has-more" : ""}`}>
          <Suspense fallback={<CircularProgress />}>
            <DynamicTable
              className="users-dynamic-table"
              title=""
              headers={displayHeaders}
              data={visibleUserData}
              downloadData={filteredUserData}
              downloadFileName={school ? "school-users" : "users"}
              actions={displayActions}
              loading={isLoading}
              searchPlaceholder="Search users"
              searchValue={searchQuery}
              onSearchChange={handleSearchChange}
              filters={[
                { key: "role", label: "Role", value: filters.role, onChange: (value) => handleFilterChange({ ...filters, role: value }), options: [{ value: "", label: "Role" }, ...roleOptions] },
                { key: "status", label: "Status", value: filters.status, onChange: (value) => handleFilterChange({ ...filters, status: value }), options: [{ value: "", label: "Status" }, { value: "ACTIVE", label: "Active" }, { value: "INACTIVE", label: "Inactive" }] },
                { key: "department", label: "Department", value: filters.department, onChange: (value) => handleFilterChange({ ...filters, department: value }), options: [{ value: "", label: "Department" }, ...departmentOptions.map((value) => ({ value, label: value }))] },
              ]}
              // toolbarEnd={<button type="button" className="users-refresh" aria-label={t("Refresh users")} onClick={() => setReloadKey((key) => key + 1)}><RefreshIcon fontSize="small" /></button>}
              pagination={{ enabled: false, pageSize: 10, itemLabel: "users" }}
              minWidth={980}
              onRowClick={canUpdateUser ? (row) => {
                setShowCreateUser(false);
                setExpandedUserId((id) => String(id) === String(row.id) ? null : row.id);
              } : undefined}
              renderExpandedRow={canUpdateUser ? (row) => String(expandedUserId) === String(row.id)
                ? <div className="users-expanded-form"><AddUser key={row.id} embedded embeddedUserId={row.id} onCancel={() => setExpandedUserId(null)} onComplete={handleUserFormComplete} /></div>
                : null : undefined}
            />
          </Suspense>
          {hasMoreUsers && <div className="users-show-more-footer">
            <span>{t("Showing")} {visibleUserData.length} {t("of")} {filteredUserData.length} {t("users")}</span>
            <button type="button" onClick={() => setVisibleUserCount((count) => count + 10)}>{t("Show more")}</button>
          </div>}
          </div>

          {/* <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                /> */}
          {/* </div> */}
        </Box>
      )}

      {/* Delete Modal - Using the common DeleteModal component */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete User!"
        message={
          <>
            {t("Are you sure you want to delete")}{" "}
            <span style={{ fontWeight: 600, color: "#d32f2f" }}>
              {selectedUser?.name || t("this user")}
            </span>
            ? {t("This action cannot be undone.")}
          </>
        }
      />

      {/* Reset Password Modal */}
      <Dialog
        open={isResetPasswordModalOpen}
        onClose={handleResetPasswordCancel}
        PaperProps={{
          style: {
            backgroundColor: "#FFFFFF",
            width: isMobile ? "90%" : isTablet ? "80%" : "594px",
            height: isMobile ? "auto" : "424px",
            borderRadius: isMobile ? "16px" : "20px",
            maxWidth: "600px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: isMobile
              ? "24px 16px"
              : isTablet
                ? "32px 40px"
                : "40px 80px",
            border: "1px solid #A5DCF3",
            boxShadow: "-2px 2px 5px 0px #00000040",
            gap: isMobile ? "24px" : "32px",
          },
        }}
      >
        <DialogTitle
          style={{
            fontFamily: "Roboto",
            fontWeight: "500",
            fontSize: isMobile ? "24px" : isTablet ? "26px" : "28px",
            lineHeight: "100%",
            letterSpacing: "0%",
            textAlign: "center",
            color: "#183059",
            padding: "0",
            margin: "0",
          }}
        >
          Reset Password
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "0",
            margin: "0",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <div>
            <h3
              style={{
                fontFamily: "Roboto",
                fontWeight: "400",
                fontSize: isMobile ? "18px" : "20px",
                lineHeight: "100%",
                letterSpacing: "0%",
                color: "#1F242E",
                margin: "0 0 12px 0",
              }}
            >
              New Password <span style={{ color: "#EE3B2B" }}>*</span>
            </h3>
            <TextField
              fullWidth
              placeholder="Enter new password"
              type={showNewPassword ? "text" : "password"}
              autoComplete="new-password"
              name="newPassword"
              value={pass.newPassword}
              onChange={handlePassChange}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{
                height: "55px",
                margin: "0",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  border: `2px solid ${passwordError ? "red" : "#183059"}`,
                  padding: "16px 24px",
                  fontSize: "16px",
                  backgroundColor: "#FFFFFF",
                  height: "55px",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "0",
                  height: "auto",
                },
                "& input::-ms-reveal, & input::-ms-clear, & input::-webkit-credentials-auto-fill-button, & input::-webkit-strong-password-auto-fill-button, & input::-webkit-reveal-button, & input::-webkit-password-reveal-button": {
                  display: "none",
                  width: 0,
                  height: 0,
                },
              }}
            />
          </div>

          <div>
            <h3
              style={{
                fontFamily: "Roboto",
                fontWeight: "400",
                fontSize: isMobile ? "18px" : "20px",
                lineHeight: "100%",
                letterSpacing: "0%",
                color: "#1F242E",
                margin: "0 0 12px 0",
              }}
            >
              Confirm New Password <span style={{ color: "#EE3B2B" }}>*</span>
            </h3>
            <TextField
              fullWidth
              placeholder="Confirm your new password"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              name="confirmPassword"
              value={pass.confirmPassword}
              onChange={handlePassChange}
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              sx={{
                height: "55px",
                margin: "0",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "16px",
                  border: `2px solid ${passwordError ? "red" : "#183059"}`,
                  padding: "16px 24px",
                  fontSize: "16px",
                  backgroundColor: "#FFFFFF",
                  height: "55px",
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                },
                "& .MuiOutlinedInput-input": {
                  padding: "0",
                  height: "auto",
                },
                "& input::-ms-reveal, & input::-ms-clear, & input::-webkit-credentials-auto-fill-button, & input::-webkit-strong-password-auto-fill-button, & input::-webkit-reveal-button, & input::-webkit-password-reveal-button": {
                  display: "none",
                  width: 0,
                  height: 0,
                },
              }}
            />
          </div>

          {passwordError && (
            <p
              style={{
                color: "red",
                margin: "8px 0 0 0",
                fontFamily: "Roboto",
                fontSize: "14px",
              }}
            >
              {passwordError}
            </p>
          )}
        </DialogContent>

        <DialogActions
          style={{
            padding: "0",
            gap: isMobile ? "20px" : "64px",
            justifyContent: "center",
            flexDirection: isMobile ? "column" : "row",
            margin: "0",
          }}
        >
          <button
            onClick={handleResetPasswordCancel}
            className="customButton_add"
            style={{
              width: isMobile ? "100%" : "122px",
              height: "55px",
              borderRadius: "8px",
              border: "1px solid #414958",
              padding: "16px 24px",
              backgroundColor: "#F2F2F3",
              fontFamily: "Roboto",
              fontSize: isMobile ? "18px" : "24px",
              fontWeight: "600",
              textTransform: "none",
              cursor: "pointer",
              color: "#000000",
            }}
          >
            Cancel
          </button>

          <button
            className="customButton_add"
            onClick={handleResetPasswordConfirm}
            style={{
              width: isMobile ? "100%" : "125px",
              height: "55px",
              borderRadius: "8px",
              border: "1px solid #414958",
              padding: "16px 24px",
              backgroundColor: "#F9E719",
              fontFamily: "Roboto",
              fontSize: isMobile ? "18px" : "24px",
              fontWeight: "600",
              textTransform: "none",
              cursor: "pointer",
              color: "#1F242E",
            }}
          >
            Confirm
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
}



